import { Ban, Calendar, Eye, Play, User } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateWIB } from "@/lib/date-utils";
import { cn } from "@/lib/utils";

import {
  formatSignedRp,
  OPNAME_DATE_FORMAT,
  signedValueClass,
} from "./stock-opname-utils";
import { STOCK_OPNAME_STATUS_LABELS, type StockOpname } from "./types";

import type { StockOpnameRowsProps } from "./stock-opname-desktop-table";

export function StockOpnameMobileList({
  rows,
  ...actions
}: StockOpnameRowsProps) {
  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        Belum ada sesi stock opname yang tercatat.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {rows.map((opname) => (
        <StockOpnameCard key={opname.id} opname={opname} {...actions} />
      ))}
    </div>
  );
}

function StockOpnameCard({
  opname,
  onOpenOpname,
  onCancelRequest,
}: Omit<StockOpnameRowsProps, "rows"> & { opname: StockOpname }) {
  const statusMeta = STOCK_OPNAME_STATUS_LABELS[opname.status];
  const isDraft = opname.status === "draft";

  return (
    <Card className="gap-2 py-3 shadow-xs">
      <CardContent className="space-y-2.5 px-4 text-xs">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="font-mono text-sm font-bold text-foreground">
              {opname.code}
            </span>
            {opname.note && (
              <p className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">
                {opname.note}
              </p>
            )}
            {opname.cancelReason && (
              <p className="mt-0.5 line-clamp-1 text-[11px] text-destructive">
                Batal: {opname.cancelReason}
              </p>
            )}
          </div>
          <Badge
            variant={statusMeta.variant}
            className="shrink-0 px-2 py-0.5 text-[10px] capitalize"
          >
            {statusMeta.label}
          </Badge>
        </div>

        <div className="space-y-2.5 border-t border-border/40 pt-2.5">
          <InfoRow
            label={
              <>
                <Calendar className="h-3 w-3" />
                Tanggal:
              </>
            }
          >
            {formatDateWIB(opname.date, OPNAME_DATE_FORMAT)}
          </InfoRow>
          <InfoRow
            label={
              <>
                <User className="h-3 w-3" />
                Petugas:
              </>
            }
          >
            {opname.createdByName || "-"}
          </InfoRow>
          <InfoRow label="Total Barang:">
            <span className="font-mono">{opname.totalItems} item</span>
          </InfoRow>
          <InfoRow label="Item Selisih:">
            {opname.discrepancyCount > 0 ? (
              <Badge
                variant="destructive"
                className="px-1.5 py-0 font-mono text-[10px]"
              >
                {opname.discrepancyCount} item
              </Badge>
            ) : (
              <span className="font-mono text-muted-foreground">0</span>
            )}
          </InfoRow>
          <InfoRow label="Nilai Selisih:">
            <span
              className={cn(
                "font-mono font-semibold",
                signedValueClass(opname.netDifferenceValue),
              )}
            >
              {formatSignedRp(opname.netDifferenceValue)}
            </span>
          </InfoRow>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/40 pt-2.5">
          {isDraft ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCancelRequest(opname)}
                className="min-h-[40px] text-xs text-muted-foreground hover:text-destructive"
              >
                <Ban className="mr-1 h-3.5 w-3.5" />
                Batal
              </Button>
              <Button
                size="sm"
                onClick={() => onOpenOpname(opname)}
                className="min-h-[40px] gap-1.5 text-xs"
              >
                <Play className="h-3.5 w-3.5 fill-current" />
                Lanjut Hitung
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenOpname(opname)}
                className="min-h-[40px] gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <Eye className="h-3.5 w-3.5" />
                Lihat Detail
              </Button>
              {opname.status !== "cancelled" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCancelRequest(opname)}
                  className="min-h-[40px] text-xs text-muted-foreground hover:text-destructive"
                >
                  <Ban className="mr-1 h-3.5 w-3.5" />
                  Batalkan
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function InfoRow({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="flex items-center gap-1 text-muted-foreground">
        {label}
      </span>
      <span className="font-medium text-foreground">{children}</span>
    </div>
  );
}
